import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  DEFAULT_OFFERS_STAGE,
  OFFERS_STAGE_OPTIONS,
  parseOffersStage,
} from "@/components/pages/analytics/front-desk/components/offers/mockData";
import { ArrowUpRight, Star } from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { translateOffersLabel } from "@/components/pages/analytics/front-desk/components/offers/translateOffersLabel";
import type { OffersStage } from "@/components/pages/analytics/front-desk/components/offers/types";
import { AnalyticsExportMenu } from "@/components/pages/analytics/front-desk/components/shared/AnalyticsExportMenu";
import { RevenueChartLegend } from "@/components/pages/analytics/front-desk/components/revenue/RevenueChartLegend";
import { SegmentPills } from "@/components/pages/analytics/front-desk/components/shared/SegmentPills";
import {
  MetricCard,
  MetricCardsGrid,
} from "@/components/pages/analytics/front-desk/components/shared";
import { useAnalyticsPage } from "@/components/pages/analytics/front-desk/context/AnalyticsPageContext";
import { toAnalyticsDateParam } from "@/components/pages/analytics/front-desk/utils/analyticsDateUtils";
import { useAdminOffersHotels } from "../../hooks/useAdminOffersHotels";
import { useAdminTenantTableExport } from "../../hooks/useAdminTenantTableExport";
import { getOffersMetricColumnsFromMetrics } from "../../config/adminTabMetricColumns";
import type {
  AdminOffersRevenueTrendData,
  AdminOffersSectionData,
} from "../../utils/adminOffersMappers";
import {
  buildAdminOffersRevenueTrendExportSection,
  downloadAdminHotelsMetricExport,
  downloadAdminOffersSectionExport,
} from "../../utils/adminTabExport";
import { AdminOffersHotelsTable } from "./AdminOffersHotelsTable";

const STAGES_WITHOUT_TOP_BLOCKS: ReadonlySet<OffersStage> = new Set([
  "dashboardCheckIn",
  "dashboardStay",
  "dashboardCheckOut",
  "preCheckOut",
]);

type RevenueTrendCardProps = {
  trend: AdminOffersRevenueTrendData;
  from: string;
  to: string;
};

function RevenueTrendCard({ trend, from, to }: RevenueTrendCardProps) {
  const { t } = useTranslation();
  const legendItems = useMemo(
    () =>
      trend.hotels.map((hotel) => ({
        key: hotel.hotelId,
        label: hotel.hotelName,
        color: hotel.color,
      })),
    [trend.hotels],
  );

  const handleExport = useCallback(
    (format: "csv" | "xlsx") => {
      if (trend.rows.length === 0) {
        return;
      }

      try {
        downloadAdminHotelsMetricExport({
          format,
          filenameBase: `tenant-offers-revenue-trend-${from}-${to}`,
          sections: [buildAdminOffersRevenueTrendExportSection(trend, t)],
        });
        toast.success(
          t("analytics.admin.export.success", "Analytics export downloaded"),
        );
      } catch {
        toast.error(
          t("analytics.admin.export.error", "Failed to export analytics"),
        );
      }
    },
    [from, t, to, trend],
  );

  return (
    <div className="rounded-xl border border-[#E5E5E5] bg-white p-4">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ArrowUpRight className="size-4 text-[#CAC19F]" />
          <h3 className="text-sm font-semibold text-[#171717]">
            {t("analytics.offers.roomUpgrade.revenueTrend", "Revenue Trend")}
          </h3>
        </div>
        <AnalyticsExportMenu
          onExport={handleExport}
          disabled={trend.rows.length === 0}
          label={t("analytics.admin.export.action", "Export")}
          className="h-8 gap-1.5 rounded-md border border-input bg-background px-3 text-sm shadow-xs hover:bg-accent hover:text-accent-foreground"
        />
      </div>

      <div className="flex flex-col gap-3">
        <div className="h-[260px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={trend.rows}
              margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="day" tickLine={false} axisLine={false} />
              <YAxis tickLine={false} axisLine={false} />
              <Tooltip />
              {trend.hotels.map((hotel) => (
                <Line
                  key={hotel.hotelId}
                  type="monotone"
                  dataKey={hotel.hotelId}
                  stroke={hotel.color}
                  strokeWidth={2}
                  dot={false}
                  name={hotel.hotelName}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        {legendItems.length > 0 ? (
          <RevenueChartLegend items={legendItems} />
        ) : null}
      </div>
    </div>
  );
}

type OffersSectionCardProps = {
  section: AdminOffersSectionData;
  loading: boolean;
  from: string;
  to: string;
};

function OffersSectionCard({
  section,
  loading,
  from,
  to,
}: OffersSectionCardProps) {
  const { t } = useTranslation();
  const columns = React.useMemo(
    () => getOffersMetricColumnsFromMetrics(section.metrics),
    [section.metrics],
  );
  const sectionTitle = translateOffersLabel(t, section.title);

  const handleExport = useCallback(
    (format: "csv" | "xlsx") => {
      if (section.hotelRows.length === 0) {
        return;
      }

      try {
        downloadAdminOffersSectionExport({
          format,
          filenameBase: `tenant-offers-${section.id}-${from}-${to}`,
          sectionTitle,
          columns,
          rows: section.hotelRows,
          t,
        });
        toast.success(
          t("analytics.admin.export.success", "Analytics export downloaded"),
        );
      } catch {
        toast.error(
          t("analytics.admin.export.error", "Failed to export analytics"),
        );
      }
    },
    [columns, from, section.hotelRows, section.id, sectionTitle, t, to],
  );

  return (
    <div className="space-y-4 rounded-xl border border-[#E5E5E5] bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Star className="size-4 text-[#CAC19F]" />
          <h3 className="text-sm font-semibold text-[#171717]">
            {sectionTitle}
          </h3>
        </div>
        <AnalyticsExportMenu
          onExport={handleExport}
          disabled={loading || section.hotelRows.length === 0}
          label={t("analytics.admin.export.action", "Export")}
          className="h-8 gap-1.5 rounded-md border border-input bg-background px-3 text-sm shadow-xs hover:bg-accent hover:text-accent-foreground"
        />
      </div>

      <MetricCardsGrid columns={4}>
        {section.metrics.map((metric) => (
          <MetricCard
            key={`${section.id}-${metric.title}`}
            title={translateOffersLabel(t, metric.title)}
            value={metric.value}
            delta={metric.delta}
            trend={metric.trend}
            icon={metric.icon}
            width="fill"
          />
        ))}
      </MetricCardsGrid>

      <AdminOffersHotelsTable
        rows={section.hotelRows}
        columns={columns}
        loading={loading}
      />
    </div>
  );
}

export const AdminOffersTab = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { dateRange } = useAnalyticsPage();
  const [searchParams, setSearchParams] = useSearchParams();
  const offersStage = parseOffersStage(searchParams.get("offersStage"));
  const {
    tenantId,
    roomUpgradeSection,
    groupSections,
    revenueTrend,
    isLoading,
    isError,
    isReady,
  } = useAdminOffersHotels(dateRange, offersStage);

  useAdminTenantTableExport({
    tab: "offers",
    dateRange,
    canExport: Boolean(tenantId) && isReady && !isLoading && !isError,
  });

  const offersStageOptions = React.useMemo(
    () =>
      OFFERS_STAGE_OPTIONS.map((option) => ({
        value: option.value,
        label: t(`analytics.offers.stages.${option.value}`, option.label),
      })),
    [t],
  );

  const shouldShowTopBlocks = !STAGES_WITHOUT_TOP_BLOCKS.has(offersStage);
  const from = toAnalyticsDateParam(dateRange.from) ?? "";
  const to = toAnalyticsDateParam(dateRange.to) ?? "";

  if (!tenantId) {
    return (
      <p className="text-zinc-500">
        {t("analytics.admin.noTenant", "Tenant is not selected.")}
      </p>
    );
  }

  if (!isReady) {
    return (
      <p className="text-zinc-500">
        {t(
          "analytics.admin.offers.selectDateRange",
          "Select a date range to view hotel offers.",
        )}
      </p>
    );
  }

  if (isError) {
    return (
      <p className="text-zinc-500">
        {t("analytics.admin.offers.loadError", "Failed to load offers data.")}
      </p>
    );
  }

  return (
    <section className="space-y-6">
      <SegmentPills
        value={offersStage}
        options={offersStageOptions}
        onChange={(value: OffersStage) => {
          setSearchParams((previousParams) => {
            const nextParams = new URLSearchParams(previousParams);
            nextParams.set("tab", "offers");
            if (value === DEFAULT_OFFERS_STAGE) {
              nextParams.delete("offersStage");
            } else {
              nextParams.set("offersStage", value);
            }
            return nextParams;
          });
        }}
        ariaLabel={t("analytics.aria.offersStages", "Offers stages")}
      />

      {shouldShowTopBlocks && roomUpgradeSection ? (
        <OffersSectionCard
          section={roomUpgradeSection}
          loading={isLoading}
          from={from}
          to={to}
        />
      ) : null}

      {shouldShowTopBlocks && revenueTrend && revenueTrend.rows.length > 0 ? (
        <RevenueTrendCard trend={revenueTrend} from={from} to={to} />
      ) : null}

      {groupSections.map((section) => (
        <OffersSectionCard
          key={section.id}
          section={section}
          loading={isLoading}
          from={from}
          to={to}
        />
      ))}
    </section>
  );
};
